// ─── Push client helper (v85b) ─────────────────────────────────────────────
// Frontend wrapper around the browser's push API + Greentime backend.
// Three jobs:
//   1. registerSW()       — register /sw.js (called once at app startup)
//   2. requestAndSubscribe() — request notification permission and create
//                              a push subscription, then ship it to the
//                              backend via registerDeviceToken. iOS quirk:
//                              must be called from a direct user-gesture
//                              handler (button click) and ONLY works for
//                              PWAs running in standalone display mode.
//   3. testPush()         — calls POST /api/push/test, returns diagnostic
//                            counts ({ devices, sent, skipped, dead, failed }).
//
// Standalone-only iOS gate: returns a clear reason string so the UI can
// explain "you need to install to the home screen first" rather than fail
// mysteriously.

import { api } from './api';

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// Convert base64 URL-safe VAPID public key to the Uint8Array format the
// browser's pushManager.subscribe expects.
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
};

export const registerSW = async () => {
  if (!('serviceWorker' in navigator)) return { ok: false, reason: 'No service worker support' };
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    return { ok: true, reg };
  } catch (e) {
    console.warn('[push] SW register failed:', e.message);
    return { ok: false, reason: e.message };
  }
};

export const getSubscriptionStatus = async () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { supported: false };
  }
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return {
    supported: true,
    permission: Notification.permission, // 'default' | 'granted' | 'denied'
    subscribed: !!sub,
  };
};

// MUST be called from a click handler (direct user gesture). iOS Safari is
// strict about this; async indirection (setState→rerender→requestPermission)
// can break the gesture chain.
export const requestAndSubscribe = async () => {
  // Hard requirement: PWA must be running standalone on iOS for push to work.
  if (isIOS() && !isStandalone()) {
    return { ok: false, reason: 'On iPhone, add Greentime to your home screen first, then open it from there.' };
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'This browser does not support push notifications.' };
  }
  // Permission first (synchronous in the gesture chain).
  let perm = Notification.permission;
  if (perm === 'default') {
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') {
    return { ok: false, reason: perm === 'denied' ? 'Notification permission was denied. Enable it in your browser/OS settings.' : 'No permission granted.' };
  }
  // Fetch VAPID public key from backend (cached after first call by the SW).
  let vapidPublic;
  try {
    const data = await api.getVapidPublicKey();
    vapidPublic = data.publicKey;
    if (!vapidPublic) return { ok: false, reason: 'Server has no VAPID public key configured.' };
  } catch (e) {
    return { ok: false, reason: `Could not fetch push key: ${e.message}` };
  }
  // Subscribe.
  const reg = await navigator.serviceWorker.ready;
  let subscription;
  try {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublic),
    });
  } catch (e) {
    return { ok: false, reason: `Subscribe failed: ${e.message}` };
  }
  // Send to backend. Token column holds the JSON-stringified subscription.
  try {
    await api.registerDeviceToken(JSON.stringify(subscription.toJSON()), 'web');
  } catch (e) {
    return { ok: false, reason: `Backend register failed: ${e.message}` };
  }
  return { ok: true };
};

// Unsubscribes any existing browser subscription then re-subscribes fresh.
// Use when the browser has a subscription but the backend has no token row
// for this user (e.g. logged in as a different account previously).
export const forceResubscribe = async () => {
  if (isIOS() && !isStandalone()) {
    return { ok: false, reason: 'Open Greentime from your home screen icon first.' };
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'This browser does not support push notifications.' };
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) await existing.unsubscribe();
  } catch (e) {
    return { ok: false, reason: `Unsubscribe failed: ${e.message}` };
  }
  // Now subscribe fresh
  return requestAndSubscribe();
};

export const testPush = async () => {
  try {
    const result = await api.testPush();
    return { ok: true, ...result };
  } catch (e) {
    return { ok: false, reason: e.message };
  }
};
